import { NetworkStatus, gql, useQuery } from '@apollo/client';
import {
  Button,
  HStack,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { EmptyCard } from '@/components/EmptyCard';
import { Sentinel } from '@/components/Sentinel';
import { FamilyCard } from '@/components/groups/FamilyCard';
import { Groups } from '@/components/placeholder-feed/Groups';
import { usePermissions } from '@/hooks/usePermissions';
import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';
import { SearchIcon } from '@/imports/ui/chakra/feather';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';

import {
  GroupSectionPage_GroupQuery,
  GroupSectionPage_GroupQueryVariables,
} from './graphql-types/GroupSectionPage_GroupQuery';

const GroupSectionPage_GroupQueryAST = gql`
  query GroupSectionPage_GroupQuery($input: TopLevelGroupsInput!) {
    topLevelGroups(input: $input) {
      data {
        ...FamilyCard_GroupSearchNodeFragment
      }
      totalCount
    }
  }
  ${FamilyCard.groupSearchNodeFragment}
`;

const LIMIT = 3;

export const GroupSectionPageContent: React.FC = () => {
  const [value, setValue] = React.useState('');
  const debouncedValue = useDebouncedValue(value, 500);

  const { error, networkStatus, data, fetchMore } = useQuery<
    GroupSectionPage_GroupQuery,
    GroupSectionPage_GroupQueryVariables
  >(GroupSectionPage_GroupQueryAST, {
    variables: {
      input: {
        data: {
          limit: LIMIT,
          offset: 0,
          filter: {
            searchTerm: debouncedValue,
          },
        },
      },
    },
    nextFetchPolicy: 'cache-first',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const permissions = usePermissions();

  const canEdit = permissions.canDoAdminStuffAndAuthenticated;
  const currLength = data?.topLevelGroups.data.length ?? 0;
  const hasMore =
    !!data?.topLevelGroups.totalCount &&
    data.topLevelGroups.totalCount > currLength;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;

  // Filter groups according to canJoin and permissions and isAutomated
  const filteredGroups = React.useMemo(() => {
    if (permissions.canDoAdminStuffAndAuthenticated) {
      return data?.topLevelGroups.data || [];
    } else {
      // Only show groups where canJoin is true and isAutomated is NOT true for regular users
      return (data?.topLevelGroups.data || []).filter(
        (groupSearchNode) =>
          groupSearchNode.group.currentUserGroupPermission?.canJoin === true &&
          groupSearchNode.group.settings?.isAutomated !== true
      );
    }
  }, [data?.topLevelGroups.data, permissions.canDoAdminStuffAndAuthenticated]);

  const defaultVariables = React.useMemo(
    () => ({
      variables: {
        input: {
          data: {
            limit: LIMIT,
            offset: currLength,
            filter: {
              searchTerm: debouncedValue,
            },
          },
        },
      },
    }),
    [currLength, debouncedValue]
  );

  if (error) {
    logError(error);
  }

  return (
    <VStack>
      <Card p="4" w="full">
        <HStack w="full" justifyContent="space-between">
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.600" />
            </InputLeftElement>
            <Input
              variant="filled"
              borderRadius="full"
              placeholder="Search group name"
              type="search"
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </InputGroup>

          {canEdit && (
            <NextLink href="/community/groups/new">
              <Button>Add Group</Button>
            </NextLink>
          )}
        </HStack>
      </Card>

      {networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.setVariables ? (
        <Groups w="full" />
      ) : filteredGroups.length > 0 ? (
        <>
          {filteredGroups.map((groupSearchNode, i) => {
            const isSecondToLastPost = i === filteredGroups.length - 2;
            return (
              <React.Fragment key={groupSearchNode.group._id}>
                <FamilyCard
                  groupSearchNode={groupSearchNode}
                  showJoinOptions={true}
                  defaultIsOpen={value !== ''}
                  isAdminSite={false}
                />
                {isSecondToLastPost && hasMore && (
                  <Sentinel
                    onIntersection={() => fetchMore(defaultVariables)}
                  />
                )}
              </React.Fragment>
            );
          })}
          {loadingMore && <Groups w="full" mt="4" />}
        </>
      ) : (
        <EmptyCard
          title={
            value === ''
              ? canEdit
                ? 'You’ve joined all groups'
                : 'There are no groups yet'
              : 'No matching groups'
          }
          // subtitle={
          //   value === ''
          //     ? canEdit
          //       ? 'Add groups to help people find things that are most relevant to them.'
          //       : 'Go back to Home to see updates from the whole community.'
          //     : canEdit
          //     ? 'Add groups to help people find things that are most relevant to them.'
          //     : 'Try less strict keywords instead.'
          // }
          // buttonText={
          //   value === ''
          //     ? canEdit
          //       ? 'Add Group'
          //       : 'Go Back to Home'
          //     : canEdit
          //     ? 'Add Group'
          //     : undefined
          // }
          // navTo={canEdit ? '/community/groups/new' : '/community'}
        />
      )}
    </VStack>
  );
};
